import * as React from 'react';
import * as R from 'ramda';
import Modal from '../../layout/modal/Modal';
import ModalHeader from '../../layout/modal/ModalHeader';
import { Str } from '../../localization/translationKeys';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import LocalBtn from '../../localization/LocalBtn';
import { COLORS } from '../../globalStyle/colors';
import { btnStyle } from '../../forms/styles/button';
import { css } from '@emotion/core';
import Spacer from '../../layout/Spacer';
import { useFormik } from 'formik';
import { onMobileMedium, onPhone } from '../../globalStyle/mediaQueries';
import { FIELDSET_DEFAULT } from '../../layout/fieldSetStyle';
import { useProjMsgText } from '../messages/hooks';
import { ProjStr } from '../messages/ProjStr';
import CreateSurveyFootnote from './CreateSurveyFootnote';
import { FeedbackSurveyQuestion, CompetencyAndQuestion } from './models';
import { DevPlanItem } from '../devPlan/models';

import CreateSurveyAddAccordian from './CreateSurveyAddAccordian';
import { useMap } from '../../core/hooks/useMap';
import { useAppState } from '../../core/redux/hooks';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useProjIds } from '../../navigation/hooks';
import { useMount } from '../../core/hooks/useMount';
import { getDevPlanItemsAction } from '../devPlan/api/getDevPlanItemsAction';


const styles = {
  root:             css`
    min-width: 780px;

    @media only screen and (max-width: 812px) {
      min-width: calc(100vw - 32px);
    }

    ${onMobileMedium} {
      max-width: 100vw;
      max-height: 100vh;
      min-width: 0;
      min-height: 0;
      width: 100vw;
      height: 100vh;
    }
  `,
  content:          css`
    justify-content: center;
    align-items: stretch;
    margin: auto 24px;

    ${onMobileMedium} {
      margin: auto 12px;
    }

    ${onPhone} {
      margin: 0;
    }
  `,
  buttonRow: css`
    margin: 10px 24px 24px;

    ${onMobileMedium} {
      margin: 10px 12px 12px;
    }  
  `,
  btnSpacer:        css`
    ${onPhone} {
      display: none;
    }
  `,
  button:           css`
    min-height: 35px;
    min-width: 132px;
  `,
};


type Props = {
  className?: string;
  dismiss: () => void;
  modalTitle: ProjStr;
  allQuestions: CompetencyAndQuestion[];
  selectedQuestions: FeedbackSurveyQuestion[];
  devItems: DevPlanItem[];
  addByCompetency: boolean;
  addQuestion: (questionId: number) => void;
  addQuestionsForCompetency: (competencyId: number) => void;
};


/**
 * CreateSurveyAddModal: a component that allows user to add questions to the feedback survey
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurveyAddModal: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const title = useProjMsgText(props.modalTitle)

  const [questionsToAdd, addQuestion, removeQuestion] = useMap<CompetencyAndQuestion>({});
  const checkboxOnClick = (question: CompetencyAndQuestion) => {
    if (!!questionsToAdd[`${question.questionId}`]) {
      removeQuestion(`${question.questionId}`);
    } else {
      addQuestion(`${question.questionId}`, question);
    }
  };

  const { handleSubmit, resetForm } = useFormik({
    initialValues:      {
    },
    onSubmit:           () => {
      R.forEach(question => props.addByCompetency
                            ? props.addQuestionsForCompetency(question.competencyId)
                            : props.addQuestion(question.questionId),
                            Object.values(questionsToAdd),
      );
      props.dismiss();
      resetForm();
    },
    validateOnChange:   false,
    enableReinitialize: true,
  });

  const projIds = useProjIds();
  const devItems = useAppState(state => state.devPlan?.items ?? []);

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projIds.projId);

  useMount(
    () => {
      if (!projIds.projId || !projIds.participantId) {
        return;
      }
      !devItems && dispatch(getDevPlanItemsAction(devPlanProjectId, projIds.participantId));
    },
  ); 

  const showFootnote = useMemo(
    () => devItems.filter(i => i.devItemCompetencyId > 0).length > 0,
    [devItems],
  )  

  return (
    <Modal css={styles.root} className={props.className} dismissOnClickOutside={false} onDismiss={props.dismiss}>
      <ModalHeader title={title} onDismiss={props.dismiss} />
      <fieldset css={[FIELDSET_DEFAULT, FLEX_COLUMN]}>
        <form onSubmit={handleSubmit}>
          <LocalDiv css={[FLEX_COLUMN, styles.content]}>
            <CreateSurveyAddAccordian 
              allQuestions={props.allQuestions}
              selectedQuestions={props.selectedQuestions}
              devItems={props.devItems}
              addByCompetency={props.addByCompetency}
              checkboxOnClick={checkboxOnClick}
            />
            {
              showFootnote && <CreateSurveyFootnote />
            }
          </LocalDiv>
          <LocalDiv css={[FLEX_ROW, styles.buttonRow]}>
            <div css={[css`flex-basis: 100%;`, styles.btnSpacer]} />
            <LocalBtn
              str={Str.Cancel}
              onClick={props.dismiss}
              css={[btnStyle.transparent(COLORS.BLUE), styles.button]}
            />
            <Spacer sizePx={10} />
            <LocalBtn
              type='submit'
              disabled={false}
              str=''
              defaultText={title}
              css={[btnStyle.primary(COLORS.BLUE), styles.button]}
            />
          </LocalDiv>
        </form>
      </fieldset>
    </Modal>
  );
};

export default CreateSurveyAddModal;
